import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Monitor, Users, ChevronLeft, ChevronRight, Maximize2, Minimize2,
    Settings, Layout, Play, X, Keyboard, Layers, Plus, Loader2,
    FolderOpen, Check
} from 'lucide-react';
import { slidesService } from '../../services/slidesService';
import { useAuth } from '../../contexts/AuthContext';
import InteractiveSidebar from '../slides/InteractiveSidebar';
import SlidesManager from '../slides/SlidesManager';
import SlidesSettings from '../slides/SlidesSettings';
import PdfSlide from '../slides/PdfSlide';
import UserProfile from '../shared/SharedUserProfile';
import { AuthModal } from '../auth/AuthModal';
import { ProfileModal } from '../shared/ProfileModal';

const SlidesTemplate = ({ bookId, bookData, customStyling }) => {
    const { user, userProfile, logout } = useAuth();
    const [session, setSession] = useState(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [showSidebar, setShowSidebar] = useState(true);
    const [isManagerOpen, setIsManagerOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [joinCode, setJoinCode] = useState('');
    const [error, setError] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);

    // Deck state
    const [decks, setDecks] = useState([]);
    const [selectedDeck, setSelectedDeck] = useState(null);
    const [slides, setSlides] = useState([]);
    const [loadingDecks, setLoadingDecks] = useState(true);
    const [showDeckPicker, setShowDeckPicker] = useState(false);
    const [deckPdfUrl, setDeckPdfUrl] = useState(null);
    const [liveSessionInfo, setLiveSessionInfo] = useState(null);
    const liveSessionUnsubRef = useRef(null);
    const liveInfoUnsubRef = useRef(null);
    const [dismissedLiveSessionId, setDismissedLiveSessionId] = useState(null);

    const isOwner = user?.uid === bookData?.ownerId;
    const headerSettings = bookData?.headerSettings || {};
    const logoHeight = headerSettings.logoHeight ?? 40;
    const logoInvert = bookData?.styling?.logoInvert || 0;

    // Load decks on mount
    useEffect(() => {
        if (bookId) {
            loadDecks();
        }
    }, [bookId]);

    // Load slides when deck is selected
    useEffect(() => {
        if (selectedDeck) {
            loadSlides(selectedDeck.id);
        }
    }, [selectedDeck]);

    useEffect(() => {
        let cancelled = false;

        const loadDeckPdfUrl = async () => {
            if (selectedDeck?.renderMode === 'pdf') {
                if (selectedDeck?.pdfStoragePath) {
                    try {
                        const url = await slidesService.getDownloadUrlFromStoragePath(selectedDeck.pdfStoragePath);
                        if (!cancelled) setDeckPdfUrl(url);
                    } catch (err) {
                        console.error('Failed to load deck PDF URL:', err);
                        if (!cancelled) setDeckPdfUrl(null);
                    }
                    return;
                }
                if (selectedDeck?.pdfUrl) {
                    setDeckPdfUrl(selectedDeck.pdfUrl);
                    return;
                }
            }
            setDeckPdfUrl(null);
        };

        loadDeckPdfUrl();
        return () => {
            cancelled = true;
        };
    }, [selectedDeck]);

    const loadDecks = async () => {
        setLoadingDecks(true);
        try {
            const decksData = await slidesService.getSlideDecks(bookId);
            setDecks(decksData);
            if (decksData.length > 0) {
                const refreshed = selectedDeck
                    ? decksData.find(deck => deck.id === selectedDeck.id)
                    : decksData[0];
                setSelectedDeck(refreshed || null);
            } else {
                setSelectedDeck(null);
            }
        } catch (err) {
            console.error('Failed to load decks:', err);
            setError('Failed to load slide decks');
        } finally {
            setLoadingDecks(false);
        }
    };

    const loadSlides = async (deckId) => {
        try {
            const slidesData = await slidesService.getSlides(bookId, deckId);
            setSlides(slidesData);
            setCurrentSlideIndex(0);
        } catch (err) {
            console.error('Failed to load slides:', err);
        }
    };

    const selectDeck = (deck) => {
        setSelectedDeck(deck);
        setShowDeckPicker(false);
        setCurrentSlideIndex(0);
    };

    // Sync slide index with session
    useEffect(() => {
        if (session?.currentSlideIndex !== undefined) {
            setCurrentSlideIndex(session.currentSlideIndex);
        }
    }, [session?.currentSlideIndex]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            switch (e.key) {
                case 'ArrowRight':
                case 'ArrowDown':
                case ' ':
                    e.preventDefault();
                    navigateSlide('next');
                    break;
                case 'ArrowLeft':
                case 'ArrowUp':
                    e.preventDefault();
                    navigateSlide('prev');
                    break;
                case 'f':
                case 'F':
                    e.preventDefault();
                    toggleFullscreen();
                    break;
                case 'Escape':
                    if (isFullscreen) {
                        exitFullscreen();
                    }
                    if (showDeckPicker) {
                        setShowDeckPicker(false);
                    }
                    break;
                case 'd':
                case 'D':
                    if (isOwner) {
                        e.preventDefault();
                        setShowDeckPicker(!showDeckPicker);
                    }
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentSlideIndex, slides.length, isFullscreen, showDeckPicker, isOwner]);

    // Fullscreen change listener
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    useEffect(() => {
        return () => {
            liveSessionUnsubRef.current?.();
            liveSessionUnsubRef.current = null;
            liveInfoUnsubRef.current?.();
            liveInfoUnsubRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!bookId || isOwner) return;
        liveInfoUnsubRef.current?.();
        liveInfoUnsubRef.current = slidesService.subscribeToLiveSessionForBook(bookId, (data) => {
            setLiveSessionInfo(data);
        });
        return () => {
            liveInfoUnsubRef.current?.();
            liveInfoUnsubRef.current = null;
        };
    }, [bookId, isOwner]);

    const handleJoinSession = async () => {
        if (!joinCode.trim()) return;
        if (!user) {
            setShowAuthModal(true);
            return;
        }
        try {
            const sessionId = await slidesService.joinByCode(joinCode.toUpperCase(), user.uid);
            const unsubscribe = await slidesService.joinAsViewer(sessionId, user.uid);
            unsubscribe((data) => setSession(data));
        } catch (err) {
            setError(err.message);
        }
    };

    const handleStartLive = async () => {
        if (!selectedDeck || session?.id) return;
        try {
            const { sessionId } = await slidesService.startPresentation(bookId, user.uid, selectedDeck.id);
            liveSessionUnsubRef.current?.();
            liveSessionUnsubRef.current = slidesService.subscribeToSession(sessionId, (data) => {
                setSession(data);
            });
        } catch (err) {
            console.error('Failed to start session:', err);
            setError('Failed to start session');
        }
    };

    const handleEndLive = async () => {
        if (!session?.id) return;
        try {
            await slidesService.endPresentation(session.id);
            liveSessionUnsubRef.current?.();
            liveSessionUnsubRef.current = null;
            setSession(null);
        } catch (err) {
            console.error('Failed to end session:', err);
        }
    };

    const navigateSlide = useCallback((direction) => {
        const newIndex = direction === 'next'
            ? Math.min(slides.length - 1, currentSlideIndex + 1)
            : Math.max(0, currentSlideIndex - 1);

        setCurrentSlideIndex(newIndex);

        // If there's an active session and user is owner, sync navigation
        if (session?.id && isOwner) {
            slidesService.navigateToSlide(session.id, newIndex);
        }
    }, [currentSlideIndex, slides.length, session, isOwner]);

    const handleSelectSlide = useCallback((index) => {
        if (index < 0 || index >= slides.length) return;
        setCurrentSlideIndex(index);
        if (session?.id && isOwner) {
            slidesService.navigateToSlide(session.id, index);
        }
    }, [slides.length, session, isOwner]);

    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                console.error('Error entering fullscreen:', err);
            });
        } else {
            document.exitFullscreen();
        }
    }, []);

    const exitFullscreen = useCallback(() => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
    }, []);

    // Deck selection screen for owners with no deck selected
    if (isOwner && !selectedDeck && !loadingDecks) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 flex items-center justify-center p-6">
                <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-white text-center">
                        <Layers className="h-16 w-16 mx-auto mb-4 opacity-90" />
                        <h1 className="text-3xl font-black tracking-tight">Select a Slide Deck</h1>
                        <p className="text-white/70 mt-2">Choose a deck to start presenting</p>
                    </div>

                    <div className="p-6">
                        {decks.length > 0 ? (
                            <div className="grid gap-4">
                                {decks.map(deck => (
                                    <button
                                        key={deck.id}
                                        onClick={() => selectDeck(deck)}
                                        className="flex items-center gap-4 p-5 bg-slate-50 hover:bg-indigo-50 border-2 border-slate-100 hover:border-indigo-300 rounded-2xl transition-all text-left group"
                                    >
                                        <div className="p-3 bg-indigo-100 rounded-xl group-hover:bg-indigo-200 transition-colors">
                                            <FolderOpen className="h-6 w-6 text-indigo-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-bold text-slate-900 text-lg">{deck.title}</h3>
                                            <p className="text-sm text-slate-500">{deck.slideCount || 0} slides</p>
                                        </div>
                                        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <Monitor className="h-16 w-16 mx-auto text-slate-200 mb-4" />
                                <p className="text-slate-500 mb-6">No slide decks found. Create one to get started.</p>
                            </div>
                        )}

                        <button
                            onClick={() => setIsManagerOpen(true)}
                            className="w-full mt-6 flex items-center justify-center gap-2 py-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg"
                        >
                            <Plus className="h-5 w-5" />
                            {decks.length > 0 ? 'Manage Decks' : 'Create New Deck'}
                        </button>
                    </div>
                </div>

                {/* Manager Modal */}
                {isManagerOpen && (
                    <SlidesManager
                        isOpen={isManagerOpen}
                        onClose={() => {
                            setIsManagerOpen(false);
                            loadDecks();
                        }}
                        bookId={bookId}
                        bookData={bookData}
                    />
                )}
            </div>
        );
    }

    // Loading state
    if (loadingDecks) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-indigo-500 mx-auto mb-4" />
                    <p className="text-white/50 font-medium">Loading presentation...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-slate-100 overflow-hidden font-sans">
            {/* Main Presentation Area */}
            <div className="flex-1 flex flex-col relative overflow-hidden bg-black">
                {liveSessionInfo && !session?.id && !isOwner && dismissedLiveSessionId !== liveSessionInfo.id && (
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 z-30 w-full max-w-xl px-4">
                        <div className="bg-white/95 backdrop-blur-md border border-indigo-200 rounded-2xl shadow-xl p-4 flex flex-col gap-3">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <p className="text-xs font-bold tracking-[0.2em] text-indigo-500 uppercase">Live Presentation</p>
                                    <p className="text-sm text-slate-700">Enter the code to join and sync with the presenter.</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="text-[10px] font-bold text-green-600 flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                        Live
                                    </div>
                                    <button
                                        onClick={() => setDismissedLiveSessionId(liveSessionInfo.id)}
                                        className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                                        title="Dismiss"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={joinCode}
                                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                    placeholder="E.G. XJ92LK"
                                    className="flex-1 text-center text-lg font-black tracking-[0.3em] py-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 outline-none uppercase placeholder:text-slate-300"
                                    maxLength={6}
                                />
                                <button
                                    onClick={handleJoinSession}
                                    className="px-5 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors"
                                >
                                    Join
                                </button>
                            </div>
                            {error && <p className="text-red-500 text-xs font-semibold">{error}</p>}
                        </div>
                    </div>
                )}
                {/* Header Overlay */}
                <div className="absolute top-0 inset-x-0 h-20 bg-gradient-to-b from-black/80 to-transparent z-20 flex items-center justify-between px-8 text-white opacity-0 hover:opacity-100 transition-opacity duration-300">
                    <div className="flex items-center gap-4">
                        {bookData?.logoUrl ? (
                            <img
                                src={bookData.logoUrl}
                                alt={bookData?.botName || bookData?.title || 'Book logo'}
                                className="max-w-[200px] object-contain"
                                style={{
                                    height: `${logoHeight}px`,
                                    filter: `invert(${logoInvert}%)`
                                }}
                            />
                        ) : (
                            <h2 className="text-xl font-bold tracking-tight">
                                {bookData?.botName || bookData?.title || selectedDeck?.title}
                            </h2>
                        )}
                        {session && (
                            <div className="bg-indigo-600 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase">
                                ROOM: {session.shortCode}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {isOwner && (
                            <>
                                {/* Deck Switcher Button */}
                                <button
                                    onClick={() => setShowDeckPicker(!showDeckPicker)}
                                    className={`p-2 rounded-full transition-colors flex items-center gap-2 ${showDeckPicker ? 'bg-indigo-600' : 'hover:bg-white/20'}`}
                                    title="Switch Deck (D)"
                                >
                                    <Layers className="h-5 w-5" />
                                    <span className="text-sm font-medium hidden sm:inline">{selectedDeck?.title}</span>
                                </button>
                                <button onClick={() => setIsManagerOpen(true)} className="p-2 hover:bg-white/20 rounded-full transition-colors" title="Manage Slides">
                                    <Layout className="h-5 w-5" />
                                </button>
                                <button onClick={() => setIsSettingsOpen(true)} className="p-2 hover:bg-white/20 rounded-full transition-colors" title="Settings">
                                    <Settings className="h-5 w-5" />
                                </button>
                                {selectedDeck && !session?.id && (
                                    <button
                                        onClick={handleStartLive}
                                        className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded-full text-xs font-bold uppercase tracking-wider hover:bg-green-600 transition-colors shadow-lg"
                                        title="Start Live"
                                    >
                                        <Play className="h-4 w-4" />
                                        Start Live
                                    </button>
                                )}
                                {session?.id && (
                                    <button
                                        onClick={handleEndLive}
                                        className="flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white rounded-full text-xs font-bold uppercase tracking-wider hover:bg-red-600 transition-colors shadow-lg"
                                        title="End Live"
                                    >
                                        <X className="h-4 w-4" />
                                        End Live
                                    </button>
                                )}
                            </>
                        )}
                        <button onClick={() => setShowSidebar(!showSidebar)} className="p-2 hover:bg-white/20 rounded-full transition-colors" title="Toggle Sidebar">
                            <Users className="h-5 w-5" />
                        </button>
                        <button onClick={toggleFullscreen} className="p-2 hover:bg-white/20 rounded-full transition-colors" title="Toggle Fullscreen (F)">
                            {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {/* Deck Picker Popup */}
                {showDeckPicker && (
                    <div className="absolute top-16 right-4 z-30 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-top-2 duration-200">
                        <div className="p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                            <h3 className="font-bold flex items-center gap-2">
                                <Layers className="h-5 w-5" />
                                Switch Slide Deck
                            </h3>
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                            {decks.map(deck => (
                                <button
                                    key={deck.id}
                                    onClick={() => selectDeck(deck)}
                                    className={`w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 ${selectedDeck?.id === deck.id ? 'bg-indigo-50' : ''
                                        }`}
                                >
                                    <div className={`p-2 rounded-lg ${selectedDeck?.id === deck.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                                        <FolderOpen className="h-4 w-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-slate-900 truncate">{deck.title}</p>
                                        <p className="text-xs text-slate-500">{deck.slideCount || 0} slides</p>
                                    </div>
                                    {selectedDeck?.id === deck.id && (
                                        <Check className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="p-3 bg-slate-50 border-t">
                            <button
                                onClick={() => {
                                    setShowDeckPicker(false);
                                    setIsManagerOpen(true);
                                }}
                                className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors text-sm"
                            >
                                <Plus className="h-4 w-4" />
                                Manage Decks
                            </button>
                        </div>
                    </div>
                )}

                {/* Click outside to close deck picker */}
                {showDeckPicker && (
                    <div
                        className="absolute inset-0 z-25"
                        onClick={() => setShowDeckPicker(false)}
                    />
                )}

                {/* Slide Content */}
                <div
                    className="flex-1 flex items-center justify-center p-4 lg:p-12 cursor-pointer"
                    onClick={(e) => {
                        if (showDeckPicker) {
                            setShowDeckPicker(false);
                            return;
                        }
                        // Click on left side = prev, right side = next
                        const rect = e.currentTarget.getBoundingClientRect();
                        const clickX = e.clientX - rect.left;
                        if (clickX < rect.width / 3) {
                            navigateSlide('prev');
                        } else if (clickX > (rect.width * 2 / 3)) {
                            navigateSlide('next');
                        }
                    }}
                >
                    {slides.length > 0 ? (
                        <div className="relative w-full h-full max-w-6xl mx-auto shadow-2xl rounded-xl overflow-hidden bg-zinc-900 flex items-center justify-center group">
                            {slides[currentSlideIndex]?.type === 'pdf' && deckPdfUrl ? (
                                <PdfSlide
                                    pdfUrl={deckPdfUrl}
                                    pageNumber={slides[currentSlideIndex]?.pageNumber || currentSlideIndex + 1}
                                    scale={1.25}
                                    className="w-full h-full"
                                />
                            ) : (
                                <img
                                    src={slides[currentSlideIndex]?.url}
                                    alt={`Slide ${currentSlideIndex + 1}`}
                                    className="max-w-full max-h-full object-contain select-none pointer-events-none"
                                    draggable={false}
                                />
                            )}

                            {/* Navigation Arrows (visible on hover) */}
                            <button
                                onClick={(e) => { e.stopPropagation(); navigateSlide('prev'); }}
                                disabled={currentSlideIndex === 0}
                                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all disabled:opacity-0 backdrop-blur-sm"
                            >
                                <ChevronLeft className="h-8 w-8" />
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); navigateSlide('next'); }}
                                disabled={currentSlideIndex === slides.length - 1}
                                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all disabled:opacity-0 backdrop-blur-sm"
                            >
                                <ChevronRight className="h-8 w-8" />
                            </button>

                            {/* Slide Number Badge */}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full text-white font-bold text-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                {currentSlideIndex + 1}
                            </div>

                            {/* Interaction Indicators */}
                            <div className="absolute top-6 left-6 flex flex-col gap-3">
                                {slides[currentSlideIndex]?.interactions?.map((int, i) => (
                                    <div key={i} className="animate-in slide-in-from-left duration-500 flex items-center gap-3 bg-white/10 backdrop-blur-xl border border-white/20 px-4 py-2 rounded-2xl text-white shadow-2xl">
                                        <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                                        <span className="text-sm font-bold">{int.title} Active</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="text-white text-center space-y-4">
                            <Monitor className="h-20 w-20 mx-auto opacity-20" />
                            <p className="text-xl font-medium opacity-50">
                                {selectedDeck ? 'This deck has no slides yet.' : 'No slides to display.'}
                            </p>
                            {isOwner && (
                                <button onClick={() => setIsManagerOpen(true)} className="px-6 py-2 bg-indigo-600 rounded-lg font-bold">
                                    Open Manager
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer/Navigation Controls */}
                <div className="absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-black/80 to-transparent z-20 flex items-center justify-between px-8 text-white opacity-0 hover:opacity-100 transition-opacity duration-300">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl font-black text-2xl tabular-nums">
                            {currentSlideIndex + 1}
                        </div>
                        <div className="text-xs text-white/50 font-medium flex items-center gap-2">
                            <Keyboard className="h-3 w-3" />
                            Use arrows or click sides
                        </div>
                    </div>

                    <div className="flex items-center gap-4 bg-white/10 backdrop-blur-xl p-2 rounded-2xl border border-white/10 shadow-2xl">
                        <button
                            onClick={() => navigateSlide('prev')}
                            disabled={currentSlideIndex === 0}
                            className="p-4 hover:bg-white/10 rounded-xl transition-all disabled:opacity-20 active:scale-90"
                        >
                            <ChevronLeft className="h-6 w-6" />
                        </button>
                        <div className="w-px h-8 bg-white/10"></div>
                        <button
                            onClick={() => navigateSlide('next')}
                            disabled={currentSlideIndex === slides.length - 1}
                            className="p-4 hover:bg-white/10 rounded-xl transition-all disabled:opacity-20 active:scale-90"
                        >
                            <ChevronRight className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="w-40 flex justify-end">
                        <button
                            onClick={toggleFullscreen}
                            className="p-3 hover:bg-white/20 rounded-full transition-colors"
                            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {/* Progress Bar */}
                {slides.length > 0 && (
                    <div className="absolute bottom-0 inset-x-0 h-1 bg-white/10 z-30">
                        <div
                            className="h-full bg-indigo-500 transition-all duration-300"
                            style={{ width: `${((currentSlideIndex + 1) / slides.length) * 100}%` }}
                        />
                    </div>
                )}

                {/* Floating User Profile */}
                <div className="absolute bottom-6 right-6 z-40">
                    <UserProfile
                        user={user}
                        userProfile={userProfile}
                        styling={customStyling}
                        bookData={bookData}
                        isOwner={isOwner}
                        logout={logout}
                        setShowAuthModal={setShowAuthModal}
                        setShowProfileModal={setShowProfileModal}
                        shouldShowUserProfile={() => true}
                    />
                </div>
            </div>

            {/* Sidebar */}
            {showSidebar && (
                <InteractiveSidebar
                    sessionId={session?.id}
                    isPresenter={isOwner}
                    currentSlideIndex={currentSlideIndex}
                    slideInteractions={slides[currentSlideIndex]?.interactions || []}
                    slides={slides}
                    deckPdfUrl={deckPdfUrl}
                    onSelectSlide={handleSelectSlide}
                />
            )}

            {/* Modals */}
            {isManagerOpen && (
                <SlidesManager
                    isOpen={isManagerOpen}
                    onClose={() => {
                        setIsManagerOpen(false);
                        loadDecks();
                        if (selectedDeck) {
                            loadSlides(selectedDeck.id);
                        }
                    }}
                    bookId={bookId}
                    bookData={bookData}
                />
            )}
            {isSettingsOpen && (
                <SlidesSettings
                    book={bookData}
                    bookId={bookId}
                    onClose={() => setIsSettingsOpen(false)}
                />
            )}

            {/* Auth Modal */}
            <AuthModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
            />

            {/* Profile Modal */}
            {showProfileModal && (
                <ProfileModal
                    onClose={() => setShowProfileModal(false)}
                    onSave={() => setShowProfileModal(false)}
                />
            )}
        </div>
    );
};

export default SlidesTemplate;
